// Figma: https://www.figma.com/design/nykbXADUTliE6d7xv6FjcQ/NEW-TSAB-Shared-Customer-Library?node-id=2780-30318
// Figma component name: AddOns row (checkbox variant)
//
// Two visual modes:
//   With icon/iconNode → full addon row (44px icon + title/info/price + checkbox)
//   Without icon       → simple row (title/info + checkbox), for other form uses

import { useRef } from 'react'
import { Field } from 'formik'
import classnames from 'classnames'

// Matches Figma Checkbox component (node 121:3590 / 121:3592)
function CheckboxIcon({ checked }) {
  if (checked) {
    return (
      <div className="bg-secondary-container border-[0.5px] border-solid border-secondary rounded-[6px] size-5 flex items-center justify-center shrink-0">
        <svg width="13" height="10" viewBox="0 0 13 10" fill="none" aria-hidden="true">
          <path d="M1.5 5L4.5 8L11 1.5" stroke="#17181A" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    )
  }
  return (
    <div className="bg-surface-container-highest border-[0.5px] border-solid border-outline rounded-[6px] size-5 shrink-0" />
  )
}

export default function CheckboxField({
  field,
  form: { setFieldValue },
  title,
  info,
  price,
  icon,
  iconNode,
  disabled,
  innerRef,
  className,
  padding = 'py-3',
  // legacy props — accepted but not used in new layout
  reverseLayout, alternateBackground, larger, secondOption, noPadding, children, disabledDescription,
  ...props
}) {
  const isChecked = Array.isArray(field.value) ? field.value.includes(props?.value) : Boolean(field.value)
  const clickRef = useRef()
  const hasIcon = icon || iconNode

  const handleClick = () => {
    if (disabled) return
    ;(innerRef || clickRef)?.current?.click()
  }

  const handleKeyDown = e => {
    if (e.keyCode === 32) e.preventDefault()
  }

  const handleKeyUp = e => {
    if (e.keyCode === 32) {
      e.preventDefault()
      setFieldValue(field.name, !isChecked)
      if (props.onClick) props.onClick()
    }
  }

  const theme = typeof CONFIG !== 'undefined' && CONFIG.svea_module ? 'svea-taxi' : 'taxi-stockholm'

  return (
    <div
      onClick={handleClick}
      className={classnames(
        'flex gap-3 items-center w-full cursor-pointer',
        padding,
        disabled && 'opacity-[0.38] pointer-events-none',
        className,
      )}
    >
      {/* Icon area — only rendered when icon/iconNode is provided */}
      {hasIcon && (
        <div className="shrink-0 size-11 overflow-clip flex items-center justify-center">
          {iconNode || (
            <img
              src={typeof icon === 'string'
                ? `/app/themes/${theme}/static/img/option-icons/${icon}.png`
                : icon}
              alt=""
              className="size-full object-contain"
            />
          )}
        </div>
      )}

      {/* Text column */}
      <div className="flex-1 flex flex-col items-start min-w-0">
        <span className="text-[17px] font-medium tracking-[-0.34px] leading-[22px] text-on-surface">{title}</span>
        {info && <span className="text-[15px] leading-5 text-on-surface-variant">{info}</span>}
        {price && <span className="text-[15px] font-medium leading-5 text-on-surface-variant">{price}</span>}
        {children}
      </div>

      {/* Checkbox */}
      <label
        ref={innerRef || clickRef}
        tabIndex={disabled ? -1 : 0}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        className="shrink-0 cursor-pointer"
        onClick={e => e.stopPropagation()}
        aria-label={title}
      >
        <CheckboxIcon checked={isChecked} />
        <Field
          type="checkbox"
          as="input"
          className="sr-only"
          name={field.name}
          onChange={field.onChange}
          onBlur={field.onBlur}
          disabled={disabled}
          {...props}
        />
      </label>
    </div>
  )
}
